export const MOVE_STEP_LABELS = [
  ["validate", "Validate patients and jobs"],
  ["replace_transcripts", "Replace both transcripts"],
  ["regenerate_target", "Regenerate the target patient's note"],
  ["erase_source_note", "Erase the source patient's note (Ready to Record if no transcript is left)"],
  ["regenerate_source", "Regenerate the source patient's note"],
];

const GENERATING_STATUSES = ["TARGET_REGENERATING", "SOURCE_REGENERATING"];
const ROLLBACK_STATUSES = ["TRANSCRIPTS_REPLACED", "TARGET_DONE", "FAILED"];

const findStep = (move, name) => (move?.steps || []).find((step) => step.step_name === name);

export const isMoveGenerating = (move) => GENERATING_STATUSES.includes(move?.status);

const stepPercent = (step) => {
  if (!step) return 0;
  if (step.status === "DONE" || step.status === "SKIPPED") return 100;
  const total = step.progress?.jobs?.length || 0;
  const isStarted = step.status === "RUNNING" || step.status === "FAILED";
  if (!isStarted || total === 0) return 0;
  return Math.round((Math.min(step.progress.current, total) / total) * 100);
};

const stepsHiddenFor = (sourceMode) =>
  ({
    [SOURCE_MODES.ERASE]: ["regenerate_source"],
    [SOURCE_MODES.LEAVE]: ["erase_source_note", "regenerate_source"],
  })[sourceMode] || [];

const eraseStepLabel = (sourceMode, clearNoteFirst) => {
  if (sourceMode === SOURCE_MODES.ERASE) return "Erase the source patient's note and set Ready to Record";
  return clearNoteFirst ? "Clear the source patient's note before rebuilding" : "Save the source note for rollback";
};

export const buildStepRows = (
  move,
  sourceMode = move?.source_mode,
  clearNoteFirst = Boolean(move?.clear_note_first),
) =>
  MOVE_STEP_LABELS.filter(([name]) => !stepsHiddenFor(sourceMode).includes(name)).map(([name, label]) => {
    const step = findStep(move, name);
    const total = step?.progress?.jobs?.length || 0;
    return {
      name,
      label: name === "erase_source_note" ? eraseStepLabel(sourceMode, clearNoteFirst) : label,
      status: (step?.status || "PENDING").toLowerCase(),
      percent: stepPercent(step),
      error: step?.error || null,
      detail: step?.progress?.errorSeenAt && step.status === "RUNNING"
        ? "checking whether the note was generated"
        : total > 1
          ? `${Math.min(step.progress.current + 1, total)} of ${total} transcripts`
          : null,
    };
  });

export const getNextMoveAction = (move) => {
  if (!move) return null;
  const replaced = findStep(move, "replace_transcripts")?.status === "DONE";
  if (move.status === "TRANSCRIPTS_REPLACED") return "target";
  if (move.status === "FAILED" && replaced && !findStep(move, "erase_source_note")) return "target";
  if (move.status === "TARGET_DONE") return "source";
  if (
    move.status === "FAILED" &&
    findStep(move, "regenerate_target")?.status === "DONE" &&
    findStep(move, "erase_source_note")
  ) {
    return "source";
  }
  return null;
};

export const canRollbackMove = (move) =>
  ROLLBACK_STATUSES.includes(move?.status) ||
  (move?.status === "DONE" && findStep(move, "regenerate_source")?.status === "SKIPPED");

export const getMoveRuns = (move) =>
  ["regenerate_target", "regenerate_source"]
    .flatMap((name) => {
      const step = findStep(move, name);
      return step?.progress?.runs?.length ? step.progress.runs : [step?.run];
    })
    .filter((run) => run?.requestId)
    .map((run) => ({ ...run, status: "running" }));

export const SOURCE_MODES = Object.freeze({
  REGENERATE: "REGENERATE",
  ERASE: "ERASE",
  LEAVE: "LEAVE",
});

export const getSourceMode = ({ leaveSourceNote, keptCount }) => {
  if (leaveSourceNote) return SOURCE_MODES.LEAVE;
  return keptCount > 0 ? SOURCE_MODES.REGENERATE : SOURCE_MODES.ERASE;
};

const FINISHED_MOVE_STATUSES = ["DONE", "ROLLED_BACK"];

export const isMoveUnfinished = (move) =>
  Boolean(move) && !FINISHED_MOVE_STATUSES.includes(move.status);

const stepLabelOf = (name) =>
  (MOVE_STEP_LABELS.find(([key]) => key === name) || [])[1] || name || null;

export const describeMove = (move, patientId) => {
  const isSource = String(move.from_patient_id) === String(patientId);
  const stamp = move.updated_at || move.created_at;
  return {
    role: isSource ? "source" : "destination",
    otherPatientId: isSource ? move.to_patient_id : move.from_patient_id,
    status: move.status,
    step: move.status === "FAILED" ? stepLabelOf(move.current_step) : null,
    requestedBy: move.requested_by || "unknown",
    when: stamp ? new Date(stamp).toLocaleString() : "unknown time",
    error: move.error || null,
  };
};

export const STEPPER_STEPS = [
  { id: "keep", label: "Keep", rowLabel: "Choose transcripts that stay", serverSteps: [] },
  { id: "moveTo", label: "Move to", rowLabel: "Destination and transcript chosen", serverSteps: [] },
  { id: "preview", label: "Preview", rowLabel: "Preview loaded", serverSteps: [] },
  { id: "replace", label: "Replace", serverSteps: ["validate", "replace_transcripts"] },
  {
    id: "regenerate",
    label: "Regenerate",
    serverSteps: ["regenerate_target", "erase_source_note", "regenerate_source"],
  },
];

const clientRow = (card, clientDone) => ({
  name: card.id,
  label: card.rowLabel,
  status: clientDone ? "done" : "pending",
  percent: clientDone ? 100 : 0,
  detail: null,
  error: null,
});

const cardStatusOf = (statuses) => {
  if (statuses.includes("failed")) return "failed";
  if (statuses.includes("running")) return "running";
  if (statuses.includes("undone")) return "undone";
  if (statuses.every((status) => status === "done" || status === "skipped")) return "done";
  return "pending";
};

export const cardProgress = (card, { rows, clientDone }) => {
  const cardRows = card.serverSteps.length
    ? rows.filter((row) => card.serverSteps.includes(row.name))
    : [clientRow(card, clientDone)];
  const total = cardRows.reduce((sum, row) => sum + row.percent, 0);
  return {
    ...card,
    percent: Math.round(total / cardRows.length),
    status: cardStatusOf(cardRows.map((row) => row.status)),
    rows: cardRows,
  };
};

export const isCardEnabled = (index, cards) =>
  cards.slice(0, index).every((card) => card.status === "done");

export const defaultCard = (cards) => {
  const index = cards.findIndex((card) => card.status !== "done");
  return index === -1 ? cards.length - 1 : index;
};
